import React, { useState, useEffect } from 'react'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { Button, Spinner } from '@nextui-org/react'
import { useAuth } from '../contexts/authContext'
import { supabase } from '../utils/supabase'
import { toast } from 'sonner'
import { toastGameActivated } from '../components/shared/CommonToasts'

// Load stripe outside of component render to avoid recreating it on renders
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

interface CheckoutFormProps {
  onSuccess: () => void
  onCancel: () => void
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ onSuccess, onCancel }) => {
  const stripe = useStripe()
  const elements = useElements()
  const { user, checkGameActivation } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [isActivating, setIsActivating] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  useEffect(() => {
    const createPaymentIntent = async () => {
      if (!user?.id) return

      try {
        setIsLoading(true)
        const { data, error } = await supabase.functions.invoke('create-payment-intent', {
          body: {
            user_id: user.id,
            email: user.email
          }
        })

        if (error) throw error

        // Store the client secret
        setClientSecret(data.clientSecret)
      } catch (error) {
        console.error('Error creating payment intent:', error)
        toast.error('Failed to initialize payment')
      } finally {
        setIsLoading(false)
      }
    }

    createPaymentIntent()
  }, [user])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || !user?.id) {
      // Stripe.js has not loaded yet, or user is not logged in
      return
    }

    setIsLoading(true)
    setPaymentError(null)

    // Confirm the payment
    const { error: submitError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + window.location.pathname
      },
      redirect: 'if_required'
    })

    if (submitError) {
      setPaymentError(submitError.message || 'An error occurred with your payment')
      setIsLoading(false)
      return
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Payment successful, activate the game
      setIsActivating(true)

      try {
        const { data, error } = await supabase.functions.invoke('activate-game', {
          body: {
            user_id: user.id,
            payment_intent_id: paymentIntent.id
          }
        })

        if (error) throw error

        if (data.success) {
          // Check if the game is now activated
          await checkGameActivation()
          toastGameActivated()
          onSuccess()
        } else {
          throw new Error(data.message || 'Failed to activate game')
        }
      } catch (error) {
        console.error('Error activating game:', error)
        toast.error('Payment was processed, but we failed to activate your game. Please contact support.')
      } finally {
        setIsActivating(false)
        setIsLoading(false)
      }
    } else {
      setPaymentError('Payment not completed. Please try again.')
      setIsLoading(false)
    }
  }

  // Check URL for existing payment status on component mount
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!stripe || !user?.id) return

      const clientId = new URLSearchParams(window.location.search).get('payment_intent_client_secret')

      if (clientId) {
        setIsLoading(true)
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientId)

        if (error) {
          console.error('Error retrieving payment intent:', error)
          toast.error('Unable to complete payment verification')
          setIsLoading(false)
          return
        }

        if (paymentIntent.status === 'succeeded') {
          // Payment successful, activate the game
          setIsActivating(true)

          try {
            const { data, error } = await supabase.functions.invoke('activate-game', {
              body: {
                user_id: user.id,
                payment_intent_id: paymentIntent.id
              }
            })

            if (error) throw error

            if (data.success) {
              // Check if the game is now activated
              await checkGameActivation()
              toastGameActivated()
              onSuccess()
            } else {
              throw new Error(data.message || 'Failed to activate game')
            }
          } catch (error) {
            console.error('Error activating game:', error)
            toast.error('Payment was processed, but we failed to activate your game. Please contact support.')
          } finally {
            setIsActivating(false)
            setIsLoading(false)
            // Clean up URL parameters
            window.history.replaceState({}, document.title, window.location.pathname)
          }
        } else {
          setIsLoading(false)
          toast.error(`Payment status: ${paymentIntent.status}. Please try again.`)
          // Clean up URL parameters
          window.history.replaceState({}, document.title, window.location.pathname)
        }
      }
    }

    checkPaymentStatus()
  }, [stripe, user?.id])

  if (isActivating) {
    return (
      <div className="p-6 bg-white rounded-lg shadow max-w-md w-full mx-auto">
        <h2 className="text-xl font-bold mb-4">Activating Your Game</h2>
        <div className="flex flex-col items-center justify-center space-y-4 my-6">
          <Spinner size="lg" />
          <p className="text-gray-600">We're activating your game...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow max-w-md w-full mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <img src="/assets/the_goat_surfer_sm.png" alt="GOAT Surfer Logo" className="w-10 h-10" />
        <h2 className="text-xl font-bold">GOAT Surfer full game</h2>
      </div>

      <div className="mb-6">
        <p className="text-gray-700 mb-2">Unlock all chapters and features of the game with a one-time payment.</p>
        <div className="text-2xl font-bold text-center my-4">€2.50</div>
        <p className="text-sm text-gray-500 text-center">Early access discount</p>
      </div>

      {isLoading && !clientSecret ? (
        <div className="flex justify-center my-6">
          <Spinner size="lg" />
        </div>
      ) : clientSecret ? (
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <PaymentElement />
          </div>

          {paymentError && <div className="p-3 mb-4 bg-red-100 text-red-700 rounded text-sm">{paymentError}</div>}

          <div className="space-y-4">
            <Button
              type="submit"
              color="primary"
              className="w-full"
              isLoading={isLoading}
              isDisabled={!stripe || !elements}
            >
              Pay Now
            </Button>
            <Button
              type="button"
              color="default"
              variant="flat"
              className="w-full"
              onClick={onCancel}
              isDisabled={isLoading}
            >
              Cancel
            </Button>

            <div className="flex items-center justify-center gap-2 mt-3 text-gray-500 text-xs">
              <img
                src="https://cdn.jsdelivr.net/gh/stripe-samples/checkout-one-time-payments@main/client/assets/powered_by_stripe.svg"
                alt="Powered by Stripe"
                className="h-4"
              />
            </div>
          </div>
        </form>
      ) : (
        <div className="text-red-500 text-center">Failed to initialize payment. Please try again.</div>
      )}
    </div>
  )
}

interface StripeCheckoutProps {
  onSuccess: () => void
  onCancel: () => void
}

export const StripeCheckout: React.FC<StripeCheckoutProps> = ({ onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const { user } = useAuth()

  useEffect(() => {
    const getClientSecret = async () => {
      if (!user?.id) return

      try {
        const { data, error } = await supabase.functions.invoke('create-payment-intent', {
          body: {
            user_id: user.id,
            email: user.email
          }
        })

        if (error) throw error

        setClientSecret(data.clientSecret)
      } catch (error) {
        console.error('Error creating payment intent:', error)
      }
    }

    getClientSecret()
  }, [user])

  return (
    <div>
      {clientSecret ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#0070f3'
              }
            }
          }}
        >
          <CheckoutForm onSuccess={onSuccess} onCancel={onCancel} />
        </Elements>
      ) : (
        <div className="p-6 bg-white rounded-lg shadow max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <img src="/assets/the_goat_surfer_sm.png" alt="GOAT Surfer Logo" className="w-10 h-10" />
            <h2 className="text-xl font-bold">GOAT Surfer full game</h2>
          </div>
          <div className="flex justify-center my-6">
            <Spinner size="lg" />
          </div>
        </div>
      )}
    </div>
  )
}

export default StripeCheckout
